import React, { useEffect, useState } from 'react';
import { SvgIcon } from '../../Icon';
import { useScratchpadContext } from '../ScratchpadContext';
import ScratchpadHeaderActions from './ScratchpadHeaderActions';
import ScratchpadSelectMenu from './ScratchpadSelectMenu/ScratchpadSelectMenu';
import * as ScratchpadHeaderUI from './ScratchpadHeaderUI';
import { CHART_NAME_MAX_LENGTH } from '../../../config/constants';
import { EditableTextInput } from '../../Form';
import { scratchpadTitleSchema } from './validation';
import ShareScratchpadPermissionsWidget from '../../SharePermissionsWidget/ShareScratchpadPermissionsWidget';
import useUser from '../../../utils/hooks/useUser';

interface IProps {
  title?: string;
}

const ScratchpadHeader = ({ title = 'New Scratchpad' }: IProps) => {
  const { handleTitleChange } = useScratchpadContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const user = useUser();

  const { currentScratchpad } = useScratchpadContext();

  // Update local title when prop changes
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  const handleSubmit = async (newTitle: string) => {
    setIsEditing(false);
    if (newTitle.trim() && newTitle !== title) {
      // Update local state immediately
      setLocalTitle(newTitle);
      try {
        await handleTitleChange(newTitle);
      } catch (error) {
        // Revert on error
        setLocalTitle(title);
        console.error('Failed to update title:', error);
      }
    }
  };

  return (
    <ScratchpadHeaderUI.Container>
      <ScratchpadHeaderUI.TitleRow>
        {isEditing ? (
          <EditableTextInput
            value={localTitle}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsEditing(false);
              setLocalTitle(title);
            }}
            limit={CHART_NAME_MAX_LENGTH}
            inputSize="XS"
            validation={scratchpadTitleSchema}
            autoFocus
          />
        ) : (
          <ScratchpadSelectMenu
            title={localTitle}
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            onClick={() => {
              setIsDropdownOpen(false);
              setIsEditing(true);
            }}
          />
        )}
        <SvgIcon
          name="caret-down"
          width={16}
          height={16}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          style={{ cursor: 'pointer' }}
        />
      </ScratchpadHeaderUI.TitleRow>

      <ScratchpadHeaderUI.ActionsContainer>
        {currentScratchpad &&
          // only owner can share scratchpad
          currentScratchpad.permission.owner.id === user.id && (
            <ShareScratchpadPermissionsWidget
              permission={currentScratchpad.permission}
              scratchpadId={currentScratchpad.id}
            />
          )}
        <ScratchpadHeaderActions />
      </ScratchpadHeaderUI.ActionsContainer>
    </ScratchpadHeaderUI.Container>
  );
};

export default ScratchpadHeader;
